// src/pages/LegalNotice.js
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

function LegalNotice() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Legal Notice
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Last Updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-8 md:p-12 space-y-8">
          
          {/* Website Owner */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Website Owner
            </h2>
            <div className="space-y-2 text-slate-700 dark:text-slate-300">
              <p>
                <strong className="text-slate-900 dark:text-white">Name:</strong> SkillSyncs Intelligence
              </p>
              <p>
                <strong className="text-slate-900 dark:text-white">Address:</strong> 108 rue de Roquebillière, 06300 Nice, France
              </p>
              <p>
                <strong className="text-slate-900 dark:text-white">Email:</strong>{" "}
                <a 
                  href="mailto:skillsyncs@outlook.com" 
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  skillsyncs@outlook.com
                </a>
              </p>
            </div>
          </section>

          {/* Publication Director */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Publication Director
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              SkillSyncs Intelligence
            </p>
          </section>

          {/* Hosting Provider */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Hosting Provider
            </h2>
            <div className="space-y-2 text-slate-700 dark:text-slate-300">
              <p>
                <strong className="text-slate-900 dark:text-white">Google Firebase</strong>
              </p>
              <p>Google LLC</p>
              <p>1600 Amphitheatre Parkway, Mountain View, CA 94043, USA</p>
            </div>
          </section>

          {/* Legal Status */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Legal Status
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              This website is operated by an individual under French auto-entrepreneur status (micro-entreprise).
            </p>
          </section>

          {/* Purpose of the Website */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Purpose of the Website
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              This platform allows users to register, create tickets, and connect with helpers who provide technical assistance.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Intellectual Property
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              All content, logos, and materials on this site belong to SkillSyncs Intelligence unless otherwise stated.
            </p>
          </section>

          {/* Personal Data */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Personal Data
            </h2>
            <div className="space-y-3 text-slate-700 dark:text-slate-300">
              <p>
                Personal data collected on this website (such as email, login information, and ticket submissions) is processed exclusively to operate the service.
              </p>
              <p>
                Users may request deletion of their account or personal data by contacting:{" "}
                <a 
                  href="mailto:skillsyncs@outlook.com" 
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  skillsyncs@outlook.com
                </a>
              </p>
              <p>
                This website complies with GDPR requirements applicable to minimal data processing for an MVP.
              </p>
            </div>
          </section>

          {/* Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Liability
            </h2>
            <p className="text-slate-700 dark:text-slate-300">
              The website owner cannot be held responsible for misuse of the platform or third-party actions.
            </p>
          </section>

          {/* Contact */}
          <section className="pt-8 border-t border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">
              Contact Us
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">
              If you have any questions regarding this Legal Notice, please contact us:
            </p>
            <div className="text-slate-700 dark:text-slate-300">
              <p>
                <strong className="text-slate-900 dark:text-white">Email:</strong>{" "}
                <a 
                  href="mailto:skillsyncs@outlook.com" 
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  skillsyncs@outlook.com
                </a>
              </p>
              <p className="mt-2">
                <strong className="text-slate-900 dark:text-white">Address:</strong> 108 rue de Roquebillière, 06300 Nice, France
              </p>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-8 flex justify-between items-center">
          <Link
            to="/"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition"
          >
            ← Back to Home
          </Link>
          <div className="flex gap-4">
            <Link
              to="/privacy"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition"
            >
              Terms of Service →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LegalNotice;

